// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	AuthSessionStorageKey,
	readStoredSessionUserId,
} from '@app/features/platform/state/auth_session/AuthSessionStorage';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {notifyAvatarCostumeResolversChanged} from '@app/features/ui/avatars/AvatarCostumeResolvers';
import {makeAutoObservable, runInAction} from 'mobx';

export const HOLIDAY_CATEGORIES = [
	'avatarCostumes',
	'emojiReactions',
	'peeks',
	'scares',
	'backgroundEffects',
	'flybys',
] as const;

export type HolidayCategory = (typeof HOLIDAY_CATEGORIES)[number];

export const HOLIDAY_PACK_IDS = ['halloween', 'festive', 'newyear'] as const;

export type HolidayPackId = (typeof HOLIDAY_PACK_IDS)[number];

export const HOLIDAY_COSTUME_PACK_IDS = ['halloween', 'festive'] as const satisfies ReadonlyArray<HolidayPackId>;

export const HOLIDAY_HEMISPHERES = ['northern', 'southern'] as const;

export type HolidayHemisphere = (typeof HOLIDAY_HEMISPHERES)[number];

export type HolidayCategoryMap = Record<HolidayCategory, boolean>;

export interface HolidayPackPreferences {
	enabled: boolean;
	categories: HolidayCategoryMap;
	preseason: boolean;
	/** Opt-in palette takeover during the pack's peak window. Off by default, unlike every other holiday preference. */
	themePreset: boolean;
}

export interface HolidayPreferencesSnapshot {
	enabled: boolean;
	hemisphere: HolidayHemisphere;
	packs: Record<HolidayPackId, HolidayPackPreferences>;
}

interface HolidayPreferencesSnapshotInput {
	enabled?: boolean;
	hemisphere?: HolidayHemisphere | null;
	packs?: Partial<Record<HolidayPackId, Partial<HolidayPackPreferences>>> | null;
}

const STORAGE_KEY_PREFIX = 'Holiday:preferences';

function getStorageKey(userId: string): string {
	return `${STORAGE_KEY_PREFIX}:${userId}`;
}

function defaultCategories(): HolidayCategoryMap {
	return {
		avatarCostumes: true,
		emojiReactions: true,
		peeks: true,
		scares: true,
		backgroundEffects: true,
		flybys: true,
	};
}

function defaultPack(): HolidayPackPreferences {
	return {
		enabled: true,
		categories: defaultCategories(),
		preseason: true,
		themePreset: false,
	};
}

function defaultPacks(): Record<HolidayPackId, HolidayPackPreferences> {
	const packs = {} as Record<HolidayPackId, HolidayPackPreferences>;
	for (const packId of HOLIDAY_PACK_IDS) {
		packs[packId] = defaultPack();
	}
	return packs;
}

function defaultHemisphere(): HolidayHemisphere {
	return 'northern';
}

function normalizeHemisphere(value: HolidayHemisphere | null | undefined): HolidayHemisphere {
	return value === 'southern' ? 'southern' : defaultHemisphere();
}

function defaultSnapshot(): HolidayPreferencesSnapshot {
	return {
		enabled: true,
		hemisphere: defaultHemisphere(),
		packs: defaultPacks(),
	};
}

function normalizeCategories(value: Partial<HolidayCategoryMap> | null | undefined): HolidayCategoryMap {
	const defaults = defaultCategories();
	if (value == null) return defaults;
	const next = {...defaults};
	for (const category of HOLIDAY_CATEGORIES) {
		const stored = value[category];
		if (typeof stored === 'boolean') next[category] = stored;
	}
	return next;
}

function normalizePack(value: Partial<HolidayPackPreferences> | null | undefined): HolidayPackPreferences {
	const defaults = defaultPack();
	if (value == null) return defaults;
	return {
		enabled: value.enabled ?? defaults.enabled,
		categories: normalizeCategories(value.categories),
		preseason: value.preseason ?? defaults.preseason,
		themePreset: value.themePreset ?? defaults.themePreset,
	};
}

function normalizePacks(
	packs: Partial<Record<HolidayPackId, Partial<HolidayPackPreferences>>> | null | undefined,
): Record<HolidayPackId, HolidayPackPreferences> {
	const next = {} as Record<HolidayPackId, HolidayPackPreferences>;
	for (const packId of HOLIDAY_PACK_IDS) {
		next[packId] = normalizePack(packs?.[packId]);
	}
	return next;
}

function normalizeSnapshot(value: HolidayPreferencesSnapshotInput | null | undefined): HolidayPreferencesSnapshot {
	const defaults = defaultSnapshot();
	if (value == null) return defaults;
	return {
		enabled: value.enabled ?? defaults.enabled,
		hemisphere: normalizeHemisphere(value.hemisphere),
		packs: normalizePacks(value.packs),
	};
}

function readStoredSnapshot(userId: string | null): HolidayPreferencesSnapshot {
	if (userId == null) return defaultSnapshot();
	try {
		const raw = AppStorage.getItem(getStorageKey(userId));
		if (raw != null) {
			return normalizeSnapshot(JSON.parse(raw) as HolidayPreferencesSnapshotInput);
		}
	} catch {
		// Fall through to defaults.
	}
	return defaultSnapshot();
}

class HolidayPreferencesState {
	enabled = true;
	hemisphere: HolidayHemisphere = defaultHemisphere();
	packs: Record<HolidayPackId, HolidayPackPreferences> = defaultPacks();
	private unsubscribeSession: (() => void) | null = null;
	private unsubscribeStorage: (() => void) | null = null;

	constructor() {
		makeAutoObservable<HolidayPreferencesState, 'unsubscribeSession' | 'unsubscribeStorage'>(
			this,
			{unsubscribeSession: false, unsubscribeStorage: false},
			{autoBind: true},
		);
		this.applyStored();
		this.initializeSessionSync();
		this.initializeStorageSync();
	}

	cleanup(): void {
		this.unsubscribeSession?.();
		this.unsubscribeSession = null;
		this.unsubscribeStorage?.();
		this.unsubscribeStorage = null;
	}

	private snapshot(): HolidayPreferencesSnapshot {
		return {
			enabled: this.enabled,
			hemisphere: this.hemisphere,
			packs: this.packs,
		};
	}

	private applyStored(userId: string | null = readStoredSessionUserId(AppStorage)): void {
		const next = readStoredSnapshot(userId);
		runInAction(() => {
			this.enabled = next.enabled;
			this.hemisphere = next.hemisphere;
			this.packs = next.packs;
		});
		notifyAvatarCostumeResolversChanged();
	}

	private persist(userId: string | null = readStoredSessionUserId(AppStorage)): void {
		if (userId == null) return;
		AppStorage.setItem(getStorageKey(userId), JSON.stringify(this.snapshot()));
	}

	private initializeSessionSync(): void {
		this.unsubscribeSession = AppStorage.subscribe(
			() => {
				this.applyStored();
			},
			{
				key: AuthSessionStorageKey.UserId,
				source: 'any',
			},
		);
	}

	private initializeStorageSync(): void {
		this.unsubscribeStorage = AppStorage.subscribe(
			(event) => {
				const userId = readStoredSessionUserId(AppStorage);
				if (userId == null) return;
				if (event.key !== null && event.key !== getStorageKey(userId)) {
					return;
				}
				this.applyStored(userId);
			},
			{source: 'external'},
		);
	}

	setEnabled(value: boolean): void {
		this.enabled = value;
		this.persist();
		notifyAvatarCostumeResolversChanged();
	}

	setPackEnabled(packId: HolidayPackId, value: boolean): void {
		const pack = normalizePack(this.packs[packId]);
		this.packs = {...this.packs, [packId]: {...pack, enabled: value}};
		this.persist();
		notifyAvatarCostumeResolversChanged();
	}

	setPackCategory(packId: HolidayPackId, category: HolidayCategory, value: boolean): void {
		const pack = normalizePack(this.packs[packId]);
		this.packs = {
			...this.packs,
			[packId]: {
				...pack,
				categories: {...pack.categories, [category]: value},
			},
		};
		this.persist();
		if (category === 'avatarCostumes') {
			notifyAvatarCostumeResolversChanged();
		}
	}

	setPackPreseason(packId: HolidayPackId, value: boolean): void {
		const pack = normalizePack(this.packs[packId]);
		this.packs = {...this.packs, [packId]: {...pack, preseason: value}};
		this.persist();
	}

	setPackThemePreset(packId: HolidayPackId, value: boolean): void {
		const pack = normalizePack(this.packs[packId]);
		this.packs = {...this.packs, [packId]: {...pack, themePreset: value}};
		this.persist();
	}

	setHemisphere(value: HolidayHemisphere): void {
		this.hemisphere = normalizeHemisphere(value);
		this.persist();
	}

	isMasterEnabled(): boolean {
		return this.enabled;
	}

	isPackEnabled(packId: HolidayPackId): boolean {
		return this.isMasterEnabled() && normalizePack(this.packs[packId]).enabled;
	}

	isCategoryEnabled(category: HolidayCategory, packId: HolidayPackId): boolean {
		if (!this.isPackEnabled(packId)) return false;
		return normalizePack(this.packs[packId]).categories[category];
	}

	isPreseasonEnabled(packId: HolidayPackId): boolean {
		if (!this.isPackEnabled(packId)) return false;
		return normalizePack(this.packs[packId]).preseason;
	}

	isThemePresetEnabled(packId: HolidayPackId): boolean {
		if (!this.isPackEnabled(packId)) return false;
		return normalizePack(this.packs[packId]).themePreset;
	}

	getHemisphere(): HolidayHemisphere {
		return this.hemisphere;
	}
}

const HolidayPreferences = new HolidayPreferencesState();

export function isHolidayMasterEnabled(): boolean {
	return HolidayPreferences.isMasterEnabled();
}

export function isHolidayPackEnabled(packId: HolidayPackId): boolean {
	return HolidayPreferences.isPackEnabled(packId);
}

export function isHolidayCategoryEnabled(category: HolidayCategory, packId: HolidayPackId): boolean {
	return HolidayPreferences.isCategoryEnabled(category, packId);
}

/** Holiday side peekers that override the year-round delete-message cat. */
export function isHolidaySidePeeksEnabled(packId: HolidayPackId = 'halloween'): boolean {
	return isHolidayCategoryEnabled('peeks', packId);
}

export function isHolidayPreseasonEnabled(packId: HolidayPackId): boolean {
	return HolidayPreferences.isPreseasonEnabled(packId);
}

/** Whether the user opted this pack's predefined palette in. Says nothing about the calendar. */
export function isHolidayThemePresetEnabled(packId: HolidayPackId): boolean {
	return HolidayPreferences.isThemePresetEnabled(packId);
}

export function getHolidayHemisphere(): HolidayHemisphere {
	return HolidayPreferences.getHemisphere();
}

export default HolidayPreferences;
